import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Textarea, cn } from '@cyphex/ui';
import type { ChannelId, ConversationId, Message } from '@cyphex/types';
/**
 * Deep imports, not the `@/features/apps` barrel: the barrel pulls in the whole
 * admin surface, and the composer sits in the initial chunk.
 */
import { SlashCommandMenu } from '@/features/apps/components/SlashCommandMenu';
import {
  parseCommandInput,
  useExecuteCommand,
} from '@/features/apps/hooks/useExecuteCommand';
import { useSlashCommands } from '@/features/apps/hooks/useSlashCommands';
import { useComposerDraft } from '../../hooks/useComposerDraft';
import { useMentionPicker } from '../../hooks/useMentionPicker';
import { useVoiceRecorder, type VoiceClipResult } from '../../hooks/useVoiceRecorder';
import type { ComposerAttachment, GifItem, StickerItem } from '../../types';
import { AttachmentTray } from './AttachmentTray';
import { ComposerToolbar } from './ComposerToolbar';
import { MentionMenu } from './MentionMenu';
import { ReplyBanner } from './ReplyBanner';
import { VoiceRecorderBar } from './VoiceRecorderBar';

export interface ComposerProps {
  conversationId: ConversationId | null;
  disabled?: boolean;
  attachments: readonly ComposerAttachment[];
  onAddFiles: (files: readonly File[]) => void;
  onRemoveAttachment: (id: string) => void;
  replyTo: Message | null;
  editing: Message | null;
  onCancelContext: () => void;
  onSend: (payload: { text: string; scheduledFor?: string }) => void;
  onSendVoice: (clip: VoiceClipResult) => void;
  onTyping: () => void;
  /**
   * Called the instant the box becomes empty again.
   *
   * Without it the only `typing.stop` ever sent was on SEND, so someone who
   * typed and then thought better of it left "X is typing" burning on every
   * other screen until the receiver's local TTL swept it — six seconds of a
   * statement that was no longer true. `onTyping` is throttled to one frame
   * per 3 s; this one is not, because there is exactly one per burst.
   */
  onStopTyping: () => void;
  /**
   * Up-arrow in an EMPTY composer edits your most recent message. Returns
   * false when there is nothing to edit, so the keystroke falls through to the
   * textarea's normal caret movement instead of being swallowed.
   */
  onEditLast?: () => boolean;
  /**
   * Channel context, when the composer is in one. Slash commands need it —
   * `/invite` and `/call` are meaningless without a channel — and it is optional
   * because the same composer serves DMs, where those commands simply refuse.
   */
  channelId?: ChannelId;
  /**
   * Voice messages, on by default. Set false where a SECOND microphone capture
   * would collide with one that is already running.
   *
   * `useVoiceRecorder` calls `getUserMedia({ audio: true })` — plain, with no
   * device id, so it ignores whatever microphone the call was told to use — and
   * the in-call chat rail is the first place this composer is reachable while a
   * WebRTC mic track is live. On iOS and Safari a second capture of the same
   * input can mute or end the first, which here means the call's own microphone
   * dies because somebody went to record a voice note.
   *
   * Suppressing the control is the honest fix at this size. Sharing one capture
   * between the call and the recorder is the real one, and it belongs in the
   * media layer, not in a prop.
   */
  voiceMessages?: boolean;
  className?: string;
}

/** Message composer: markdown, attachments, emoji/GIF/stickers, voice, schedule. */
export function Composer({
  conversationId,
  disabled = false,
  attachments,
  onAddFiles,
  onRemoveAttachment,
  replyTo,
  editing,
  onCancelContext,
  onSend,
  onSendVoice,
  onTyping,
  onStopTyping,
  onEditLast,
  channelId,
  voiceMessages = true,
  className,
}: ComposerProps) {
  const { draft, setText, clear } = useComposerDraft(conversationId);
  const input = useRef<HTMLTextAreaElement | null>(null);
  const recorder = useVoiceRecorder();

  /* Where the caret is. The `@` picker completes the word BEFORE it, and a
     textarea only says where that is when asked. */
  const [caret, setCaret] = useState(0);
  const mention = useMentionPicker(conversationId, draft.text, caret, !disabled);

  // Autocomplete is suppressed while EDITING: an edit rewrites an existing
  // message, and a message that already starts with a slash would pop a command
  // menu that could never run against it.
  const slash = useSlashCommands(draft.text, !disabled && editing === null);
  const command = useExecuteCommand();

  useEffect(() => {
    if (editing) setText(editing.bodyText ?? '');
    // Only reset the box when the edit target itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  useEffect(() => {
    const element = input.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 176)}px`;
  }, [draft.text]);

  const canSend =
    !disabled && (draft.text.trim().length > 0 || attachments.some((item) => item.status === 'ready'));

  /**
   * Enter routes to ONE of two places.
   *
   * A draft that starts with `/` and names a command runs the command instead of
   * being sent as a message. That branch is here rather than on the server for a
   * reason worth stating: if the client sent `/away` as text and the API decided
   * afterwards, then every message beginning with a slash — a path, a fraction,
   * a regex — would need server-side disambiguation, and a person quoting
   * `/etc/hosts` would occasionally change their own presence.
   *
   * A trailing space is NOT a scheduled send, and scheduling a command makes no
   * sense, so the command branch ignores `scheduledFor` entirely.
   */
  const submit = useCallback(
    (scheduledFor?: string) => {
      if (!canSend) return;
      const text = draft.text.trim();

      const parsed = scheduledFor === undefined ? parseCommandInput(text) : null;
      if (parsed !== null && parsed.command.length > 0) {
        command.run({
          command: parsed.command,
          text: parsed.text,
          ...(channelId === undefined ? {} : { channelId }),
          ...(conversationId === null ? {} : { conversationId }),
        });
        clear();
        return;
      }

      onSend(scheduledFor ? { text, scheduledFor } : { text });
      clear();
    },
    [canSend, channelId, clear, command, conversationId, draft.text, onSend],
  );

  /** Replace the whole draft with the accepted command and re-focus. */
  const acceptCommand = useCallback(
    (chosen?: Parameters<typeof slash.accept>[0]) => {
      const next = slash.accept(chosen);
      if (next === null) return;
      setText(next);
      requestAnimationFrame(() => input.current?.focus());
    },
    [setText, slash],
  );

  /** Put the chosen person's name where the `@word` was, caret just after it. */
  const acceptMention = useCallback(
    (chosen?: Parameters<typeof mention.accept>[0]) => {
      const next = mention.accept(chosen);
      if (next === null) return;
      setText(next.text);
      setCaret(next.caret);
      requestAnimationFrame(() => {
        input.current?.focus();
        input.current?.setSelectionRange(next.caret, next.caret);
      });
    },
    [mention, setText],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // The `@` list owns the navigation keys while it is open - ahead of the
      // slash menu and of Enter-to-send, or Enter would post "@may" as typed.
      if (mention.isOpen) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          mention.move(1);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          mention.move(-1);
          return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          acceptMention();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          mention.close();
          return;
        }
      }

      // The slash menu owns the navigation keys WHILE IT IS OPEN, and only then.
      // Handled before everything else so Enter picks the highlighted command
      // rather than sending `/depl` as a message.
      if (slash.isOpen) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          slash.move(1);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          slash.move(-1);
          return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          acceptCommand();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          slash.close();
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submit();
        return;
      }
      if (event.key === 'Escape' && (replyTo || editing)) {
        onCancelContext();
        return;
      }
      // Up-arrow edits the last message — but ONLY from a genuinely empty box
      // and only when not already editing, or it would hijack caret movement
      // inside a half-written paragraph.
      if (
        event.key === 'ArrowUp' &&
        !editing &&
        draft.text.length === 0 &&
        !event.shiftKey &&
        !event.altKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        onEditLast?.()
      ) {
        event.preventDefault();
      }
    },
    [
      acceptCommand, acceptMention, draft.text.length, editing, mention, onCancelContext,
      onEditLast, replyTo, slash, submit,
    ],
  );

  const insert = useCallback(
    (value: string) => {
      const element = input.current;
      const start = element?.selectionStart ?? draft.text.length;
      const end = element?.selectionEnd ?? draft.text.length;
      setText(`${draft.text.slice(0, start)}${value}${draft.text.slice(end)}`);
      requestAnimationFrame(() => element?.focus());
    },
    [draft.text, setText],
  );

  const wrap = useCallback(
    (prefix: string, suffix = prefix) => {
      const element = input.current;
      const start = element?.selectionStart ?? 0;
      const end = element?.selectionEnd ?? 0;
      const selected = draft.text.slice(start, end);
      setText(`${draft.text.slice(0, start)}${prefix}${selected}${suffix}${draft.text.slice(end)}`);
      requestAnimationFrame(() => element?.focus());
    },
    [draft.text, setText],
  );

  const stopAndSend = useCallback(() => {
    void recorder.stop().then((clip) => {
      if (clip) onSendVoice(clip);
    });
  }, [onSendVoice, recorder]);

  return (
    <div
      className={cn(
        // `relative` anchors the slash popover, which opens UPWARD: the composer
        // already sits at the bottom of the viewport.
        'relative shrink-0 rounded-xl border border-border bg-card shadow-card focus-within:border-primary/50',
        className,
      )}
    >
      <SlashCommandMenu
        commands={slash.suggestions}
        activeIndex={slash.activeIndex}
        onPick={(picked) => acceptCommand(picked)}
      />
      <MentionMenu
        targets={mention.suggestions}
        activeIndex={mention.activeIndex}
        onPick={(picked) => acceptMention(picked)}
        onHover={mention.setActive}
      />

      {editing ? <ReplyBanner message={editing} mode="edit" onCancel={onCancelContext} /> : null}
      {!editing && replyTo ? <ReplyBanner message={replyTo} mode="reply" onCancel={onCancelContext} /> : null}

      {recorder.isRecording ? (
        <VoiceRecorderBar
          elapsedMs={recorder.elapsedMs}
          level={recorder.level}
          onCancel={recorder.cancel}
          onSend={stopAndSend}
        />
      ) : (
        <>
          <div className="px-3 pt-3">
            <Textarea
              ref={input}
              variant="composer"
              hideLabel
              label="Message"
              placeholder={
                disabled
                  ? 'You cannot post here'
                  : 'Type a message, @ to mention someone, or / for a command.'
              }
              value={draft.text}
              disabled={disabled}
              maxRows={8}
              onChange={(event) => {
                const next = event.target.value;
                setText(next);
                setCaret(event.target.selectionStart ?? next.length);
                // Emptying the box is a typing STOP, not another start.
                if (next.trim().length === 0) onStopTyping();
                else onTyping();
              }}
              onKeyDown={onKeyDown}
              onSelect={(event) => setCaret(event.currentTarget.selectionStart ?? 0)}
            />
          </div>

          <AttachmentTray attachments={attachments} onRemove={onRemoveAttachment} />

          <ComposerToolbar
            canSend={canSend}
            voiceMessages={voiceMessages}
            onWrap={wrap}
            onEmoji={insert}
            onGif={(gif: GifItem) => insert(`![${gif.title}](${gif.url})`)}
            onSticker={(sticker: StickerItem) => insert(sticker.emoji)}
            onFiles={onAddFiles}
            onRecord={() => void recorder.start()}
            onSchedule={(iso) => submit(iso)}
            onSend={() => submit()}
          />
        </>
      )}
    </div>
  );
}
